/**
 * @file EmptyState.tsx
 *
 * Centred empty-state block for tabs with no data to show (e.g. no products, or
 * no results after filtering). Presentational: copy is passed in localized, with
 * an optional icon and a single primary action.
 */

import type { ReactNode } from 'react';
import styles from './EmptyState.module.css';

interface EmptyStateProps {
  title: string;
  description: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  /** Optional node rendered before the action label (e.g. a leading icon). */
  labelPrefix?: ReactNode;
  /** `data-testid` for the root; defaults to `empty-state`. */
  testId?: string;
}

/**
 * Renders a centred empty state with an optional icon and action.
 *
 * @param props - Localized title/description, optional icon and action.
 *
 * @returns The empty-state element.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  labelPrefix,
  testId = 'empty-state',
}: EmptyStateProps) {
  return (
    <div
      className={`${styles.container} gv-flex gv-flex-col gv-items-center gv-justify-center gv-h-full gv-gap-lg`}
      data-testid={testId}
    >
      <div className="gv-flex gv-flex-col gv-items-center gv-justify-center gv-gap-sm">
        {icon && <div className={styles.iconWrap}>{icon}</div>}
        <h3 className="gv-heading-sm">{title}</h3>
        <p className="gv-text-md">{description}</p>
      </div>
      {action && (
        <button
          className="gv-button gv-button-primary"
          type="button"
          data-testid={`${testId}-action`}
          onClick={action.onClick}
        >
          {labelPrefix}
          {action.label}
        </button>
      )}
    </div>
  );
}
